import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useTeam } from '@/hooks/useTeam'
import { useNavigate } from 'react-router-dom'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import ParentsList from '@/components/dashboard/ParentsList'
import ParentForm from '@/components/dashboard/ParentForm'
import UpgradePrompt from '@/components/common/UpgradePrompt'
import Loading from '@/components/common/Loading'

function ParentManager() {
  const { team, loading: teamLoading } = useTeam()
  const navigate = useNavigate()
  
  const [parents, setParents] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingParent, setEditingParent] = useState(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false)

  useEffect(() => {
    if (team) {
      fetchParents()
    }
  }, [team])

  const fetchParents = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('parents')
        .select('*')
        .eq('team_id', team.id)
        .order('created_at', { ascending: true })

      if (error) throw error
      setParents(data || [])
    } catch (err) {
      console.error('Error fetching parents:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleAddParent = () => {
    // Check tier limits
    if (team.season_tier === 'free' && parents.length >= 25) {
      setShowUpgradePrompt(true)
      return
    }

    setShowForm(true)
  }

  const handleParentAdded = () => {
    fetchParents()
    setShowForm(false)
    setEditingParent(null)
  }

  const handleParentDeleted = () => {
    fetchParents()
  }

  const handleEditParent = (parent) => {
    setEditingParent(parent)
    setShowForm(true)
  }

  const handleImportParents = async (emailList) => {
    // Parse emails from textarea (comma or newline separated)
    const emails = emailList
      .split(/[\n,]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'))
    
    if (emails.length === 0) {
      alert('No valid emails found')
      return
    }

    // Check tier limits
    if (team.season_tier === 'free' && parents.length + emails.length > 25) {
      alert(`Free tier allows 25 parent emails. You currently have ${parents.length} and are trying to add ${emails.length}.`)
      return
    }

    try {
      const parentsToInsert = emails.map(email => ({
        team_id: team.id,
        email: email
      }))

      const { error } = await supabase
        .from('parents')
        .insert(parentsToInsert)

      if (error) throw error

      fetchParents()
      setShowImportModal(false)
    } catch (err) {
      console.error('Error importing parents:', err)
      alert('Failed to import some emails. Duplicate emails were skipped.')
    }
  }

  if (teamLoading || loading) {
    return <Loading text="Loading parents..." />
  }

  if (!team) {
    navigate('/team-setup')
    return null
  }

  const isFreeAndAtLimit = team.season_tier === 'free' && parents.length >= 25

  return (
    <div className="min-h-screen bg-brand-cream py-8">
      <Container>
        <div className="max-w-4xl mx-auto">
          {/* Navigation Bar */}
          <div className="mb-6">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="secondary"
              className="text-sm"
            >
              ← Back to Dashboard
            </Button>
          </div>

          <Card>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h1 className="font-heading text-3xl font-bold mb-2">
                  Parent Emails
                </h1>
                <p className="text-brand-slate">
                  {team.season_tier === 'free' ? (
                    <span>
                      {parents.length} of 25 parent emails used (Free Tier)
                    </span>
                  ) : (
                    <span>{parents.length} parent emails (Unlimited)</span>
                  )}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => setShowImportModal(true)}
                  variant="secondary"
                  disabled={isFreeAndAtLimit}
                >
                  Import Parents
                </Button>
                <Button
                  onClick={handleAddParent}
                  variant="primary"
                  disabled={isFreeAndAtLimit}
                >
                  Add Parent
                </Button>
              </div>
            </div>

            {isFreeAndAtLimit && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  You've reached the 25-email limit on the Free Tier.{' '}
                  <button
                    onClick={() => navigate('/pricing')}
                    className="font-medium underline"
                  >
                    Upgrade to Pro
                  </button>{' '}
                  for unlimited parent emails.
                </p>
              </div>
            )}

            {parents.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-brand-slate mb-4">
                  No parent emails added yet.
                </p>
                <div className="flex gap-3 justify-center">
                  <Button onClick={handleAddParent} variant="primary">
                    Add Your First Parent
                  </Button>
                  <Button onClick={() => setShowImportModal(true)} variant="secondary">
                    Import Multiple
                  </Button>
                </div>
              </div>
            ) : (
              <ParentsList
                parents={parents}
                onDelete={handleParentDeleted}
                onEdit={handleEditParent}
                teamId={team.id}
              />
            )}
          </Card>

          {showForm && (
            <ParentForm
              teamId={team.id}
              parent={editingParent}
              onSuccess={handleParentAdded}
              onCancel={() => {
                setShowForm(false)
                setEditingParent(null)
              }}
            />
          )}

          {showImportModal && (
            <ImportParentsModal
              onImport={handleImportParents}
              onCancel={() => setShowImportModal(false)}
            />
          )}

          {showUpgradePrompt && (
            <UpgradePrompt
              title="Upgrade to Pro"
              message="You've reached the 25-email limit on the Free Tier. Upgrade to Pro for $49/season to add unlimited parent emails."
              onUpgrade={() => navigate('/pricing')}
              onClose={() => setShowUpgradePrompt(false)}
            />
          )}
        </div>
      </Container>
    </div>
  )
}

// Import Parents Modal Component
function ImportParentsModal({ onImport, onCancel }) {
  const [emailList, setEmailList] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    await onImport(emailList)
    setLoading(false)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="max-w-lg w-full">
        <h2 className="font-heading text-2xl font-bold mb-4">Import Parent Emails</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brand-charcoal mb-2">
              Paste email addresses (one per line or comma-separated)
            </label>
            <textarea
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green font-mono text-sm"
              rows="8"
              placeholder="parent1@example.com&#10;parent2@example.com&#10;parent3@example.com"
              value={emailList}
              onChange={(e) => setEmailList(e.target.value)}
              required
              disabled={loading}
            />
            <p className="mt-2 text-xs text-brand-slate">
              Tip: Copy emails from a spreadsheet column or email list. Duplicate emails will be automatically skipped.
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Importing...' : 'Import'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

export default ParentManager

